import { jsx as _jsx } from "react/jsx-runtime";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { forwardRef } from "react";
import { cn } from "../lib/cn";
/**
 * The variant map — a `cva` recipe, never a switch/if chain.
 *
 * Add a variant here; never fork a Button in an app. Exported so a non-button
 * element (a router `Link` rendered through `asChild`, an `<a>` in a cell) can
 * borrow the exact same classes rather than approximating them.
 */
export const buttonVariants = cva([
    "inline-flex items-center justify-center",
    "rounded-control font-medium select-none",
    "transition-colors",
    "focus-visible:outline-none focus-visible:shadow-focus",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
].join(" "), {
    variants: {
        variant: {
            primary: "bg-brand text-brand-foreground hover:bg-brand/90",
            secondary: "bg-surface-raised text-primary border border-border hover:bg-brand-subtle",
            outline: "bg-transparent text-primary border border-border hover:bg-brand-subtle",
            ghost: "bg-transparent text-primary hover:bg-brand-subtle",
            destructive: "bg-danger text-danger-foreground hover:bg-danger/90",
            link: "bg-transparent text-brand underline-offset-4 hover:underline",
        },
        /**
         * Every size shares ONE height floor, `h-control`, which resolves
         * `--control-height` — 44px until an app opts into a density on
         * `AppShell` (docs/018-ux-standards.md, "Density"). Sizes differ in
         * padding and type size, never in height.
         *
         * The floor is therefore no longer a fixed 44px: under `compact` it is
         * 32px and under `default` 36px. That still clears WCAG 2.2 AA, which
         * asks for 24x24 (2.5.8 Target Size (Minimum)); 44px is the AAA target
         * (2.5.5), and `touch` — the visitor kiosk — is what provides it.
         *
         * `min-w-11` on `icon` is untouched: it is a WIDTH floor, and this slice
         * only tokenised height. An icon button under a non-default density is
         * consequently wider than it is tall until a matching width token lands.
         */
        size: {
            sm: "h-control text-sm px-3 gap-2",
            md: "h-control text-sm px-4 gap-2",
            lg: "h-control text-base px-6 gap-2",
            icon: "h-control min-w-11 p-0",
        },
    },
    defaultVariants: {
        variant: "primary",
        size: "md",
    },
});
export const Button = forwardRef(function Button({ className, variant = "primary", size = "md", asChild = false, ...props }, ref) {
    const Component = asChild ? Slot : "button";
    return (_jsx(Component, { ref: ref, className: cn(buttonVariants({ variant, size }), className), ...props }));
});
Button.displayName = "Button";
//# sourceMappingURL=button.js.map